// Cours premium Plateau n°3 — "Tu accélères • Tu freines • Tu évites • Tu enchaînes"
// Plateau A2 — 14 chapitres pédagogiques (sans visuels)
// Session 3 : changer de dimension — passer de la basse allure à l'allure élevée

export const cours = {
  slug: 'troisieme-plateau-a2',
  title: 'Ma 3ᵉ séance plateau A2',
  subtitle: 'Tu accélères • Tu freines • Tu évites • Tu enchaînes',
  duree: '2 heures — plateau école',
  cover: '/images/cours/plateau/session-3.png',
  mantra: 'REGARDE → PRÉPARE → ACCÉLÈRE → DÉCIDE → AGIS → STABILISE',
  intro: "Après deux sessions, tu commences à maîtriser ta moto à basse allure. Ton point de patinage est mieux ressenti, ton regard progresse et tes commandes deviennent plus naturelles. Aujourd'hui, nous changeons de dimension : tu dois pouvoir passer d'une conduite lente et précise à une allure plus importante, puis freiner ou modifier rapidement ta trajectoire tout en conservant la maîtrise de ta moto.",
  sections: [
    {
      num: '01', kicker: 'Point de départ', title: 'Ton bilan n°2 détermine ton travail',
      lead: "Avant de démarrer, nous reprenons ton dernier bilan. Aujourd'hui, je vais également observer quelque chose de nouveau : es-tu capable de rester précis lorsque l'allure augmente ?",
      type: 'list',
      list: [
        { label: 'DEVENU NATUREL', desc: "Qu'est-ce qui est devenu naturel ?" },
        { label: 'ENCORE COÛTEUX', desc: "Qu'est-ce qui te demande encore beaucoup d'attention ?" },
        { label: 'BASSE ALLURE', desc: "Où rencontres-tu encore des difficultés à basse allure ?" },
        { label: 'ALLURE QUI MONTE', desc: "Comment te sens-tu lorsque l'allure augmente ?" },
        { label: 'FREINAGE', desc: "Comment te sens-tu au freinage ?" },
      ],
      retenir: 'ES-TU CAPABLE DE RESTER PRÉCIS LORSQUE L\'ALLURE AUGMENTE ?',
    },
    {
      num: '02', kicker: 'Réactivation', title: 'Tu retrouves tes automatismes à basse allure',
      lead: "Nous commençons par quelques exercices connus : point de patinage, allure lente, portes, changements de direction, demi-tour, arrêt. Je veux voir si tu retrouves spontanément tes acquis, sans que je te réexplique tout.",
      type: 'list',
      list: [
        { label: 'REGARD', desc: 'Il repart tout de suite haut et anticipateur.' },
        { label: 'MOTRICITÉ', desc: 'Douce, continue, dans la zone du point de patinage.' },
        { label: 'DOSAGE', desc: 'Accélérateur/embrayage/frein arrière coordonnés.' },
        { label: 'FREIN ARRIÈRE', desc: 'Utilisé avec finesse à basse allure.' },
        { label: 'SOUPLESSE', desc: 'Bras, bassin et regard disponibles.' },
        { label: 'TRAJECTOIRE', desc: 'Choisie et tenue, sans hésitation.' },
      ],
      retenir: 'UNE COMPÉTENCE ACQUISE DOIT POUVOIR ÊTRE RÉUTILISÉE RAPIDEMENT.',
    },
    {
      num: '03', kicker: 'Lent → dynamique', title: 'Tu passes du lent au dynamique',
      lead: "Tu ne dois pas être à l'aise uniquement dans une seule zone de vitesse. Tu apprends à modifier ton allure tout en conservant ton regard, ta trajectoire, ta position et ta maîtrise des commandes.",
      type: 'timeline',
      steps: [
        { time: '1', label: 'LENT', desc: 'Départ à faible allure, stable et régulier.' },
        { time: '2', label: 'ACCÉLÉRATION', desc: 'Ouverture progressive de l\'accélérateur.' },
        { time: '3', label: 'MONTÉE DE RAPPORTS', desc: 'Passages nets, sans casser la motricité.' },
        { time: '4', label: 'ALLURE STABILISÉE', desc: 'Trajectoire tenue, regard porté plus loin.' },
        { time: '5', label: 'DÉCÉLÉRATION', desc: 'Freinage progressif, rétrogradage si besoin.' },
        { time: '6', label: 'RETOUR AU LENT', desc: 'Moto de nouveau stable à basse allure.' },
      ],
      retenir: 'CHANGER D\'ALLURE SANS PERDRE NI TON REGARD NI TA TRAJECTOIRE.',
    },
    {
      num: '04', kicker: 'Doucement sur les gaz — à plus haute allure', title: 'Tu accélères sans te désorganiser',
      lead: "Accélérer ne signifie pas simplement tourner davantage la poignée. Ici on transpose la notion « doucement sur les gaz » (leçon 1) à des allures plus élevées : tu ouvres l'accélérateur avec fluidité et progressivité, pas d'un coup. Quand tu augmentes ton allure, tout doit rester organisé — regard loin, trajectoire stable, accélération accompagnée — et tu continues à piloter pendant que tu accélères.",
      type: 'cards3',
      cards: [
        { title: 'REGARD LOIN', desc: 'Il reste projeté devant, pas fixé sur le compteur.' },
        { title: 'TRAJECTOIRE STABLE', desc: 'La ligne choisie ne dévie pas pendant l\'accélération.' },
        { title: 'GESTES INTÉGRÉS', desc: 'Rapport, poignée, position : coordonnés sans effort — l\'équilibre en phase (leçon 1).' },
      ],
      retenir: 'DOUCEMENT SUR LES GAZ = FLUIDITÉ ET PROGRESSIVITÉ, MÊME QUAND ON ACCÉLÈRE FORT.',
    },
    {
      num: '05', kicker: 'Rapports fluides', title: 'Tes changements de rapports deviennent plus naturels',
      lead: "Tu dois pouvoir accélérer → changer de rapport → poursuivre ton accélération sans perturber ta trajectoire. Quand tu ralentis, tu apprends aussi à coordonner décélération, freinage, embrayage et sélection du bon rapport.",
      type: 'cards3',
      cards: [
        { title: 'MONTÉE', desc: 'Rapport engagé sans à-coup, motricité conservée.' },
        { title: 'RÉTROGRADAGE', desc: 'Coordonné avec freinage et embrayage.' },
        { title: 'REGARD LIBRE', desc: 'Tu ne cherches ni ton pied ni ton sélecteur.' },
      ],
      retenir: 'TON PIED TRAVAILLE. TON REGARD RESTE SUR LA ROUTE.',
    },
    {
      num: '06', kicker: 'Freinage préparé', title: 'Tu prépares ton freinage avant de freiner',
      lead: "Un freinage maîtrisé commence avant l'action sur les commandes. Nous voulons remplacer la boucle « surprise → action brutale → désorganisation » par « information → préparation → action → contrôle ».",
      type: 'timeline',
      steps: [
        { time: '1', label: 'INFORMATION', desc: 'Je repère la zone de ralentissement ou d\'arrêt.' },
        { time: '2', label: 'PRÉPARATION', desc: 'Trajectoire stabilisée, regard porté loin.' },
        { time: '3', label: 'ACTION', desc: 'Freinage déclenché de manière progressive.' },
        { time: '4', label: 'CONTRÔLE', desc: 'Je conserve la maîtrise de la moto jusqu\'à l\'arrêt.' },
      ],
      retenir: 'UN FREINAGE MAÎTRISÉ COMMENCE AVANT L\'ACTION SUR LES COMMANDES.',
    },
    {
      num: '07', kicker: 'Efficacité progressive', title: 'Tu augmentes progressivement l\'efficacité de ton freinage',
      lead: "Nous augmentons progressivement les exigences, pas brutalement. Ton freinage devient plus important au fur et à mesure que ta technique le permet. Le comportement de ta moto évolue avec le transfert de charge : tu accompagnes ce comportement sans te crisper.",
      type: 'list',
      list: [
        { label: 'TRANSFERT DE CHARGE', desc: 'Davantage de charge se reporte vers l\'avant.' },
        { label: 'PROGRESSIVITÉ', desc: 'Augmentation d\'intensité, jamais action brutale.' },
        { label: 'REGARD HAUT', desc: 'Il reste porté loin, jamais sur la roue avant.' },
        { label: 'TRAJECTOIRE', desc: 'Elle reste maîtrisée pendant tout le freinage.' },
      ],
      retenir: 'L\'OBJECTIF N\'EST PAS D\'ÉCRASER UNE COMMANDE. C\'EST D\'OBTENIR UNE DÉCÉLÉRATION EFFICACE ET MAÎTRISÉE.',
    },
    {
      num: '08', kicker: 'Freinage précis', title: 'Tu freines à l\'endroit prévu',
      lead: "Il ne suffit plus de réussir à t'arrêter : tu dois déclencher au bon moment et maîtriser ta zone d'arrêt. Passage après passage, nous analysons ensemble.",
      type: 'list',
      list: [
        { label: 'PRÉPARATION AMONT', desc: 'Ai-je préparé suffisamment tôt ?' },
        { label: 'DÉCLENCHEMENT', desc: 'Ai-je déclenché au bon moment ?' },
        { label: 'PROGRESSIVITÉ', desc: 'Mon action était-elle progressive ?' },
        { label: 'REGARD', desc: 'Est-il resté loin devant ?' },
        { label: 'TRAJECTOIRE', desc: 'Ai-je conservé la maîtrise de ma trajectoire ?' },
      ],
      retenir: 'NOUS CHERCHONS À RENDRE LA RÉUSSITE REPRODUCTIBLE, PAS UNIQUEMENT À RÉUSSIR UNE FOIS.',
    },
    {
      num: '09', kicker: 'Freiner ≠ éviter', title: 'Tu comprends la différence entre freiner et éviter',
      lead: "Face à un obstacle, deux grandes possibilités selon la situation : ralentir/freiner ou modifier ta trajectoire pour éviter. Ce ne sont pas les mêmes actions, et tu ne dois pas improviser une technique que tu ne maîtrises pas.",
      type: 'cards3',
      cards: [
        { title: 'FREINER', desc: 'Action sur les commandes pour ralentir en ligne.' },
        { title: 'ÉVITER', desc: 'Action rapide sur la trajectoire pour contourner.' },
        { title: 'CHOISIR', desc: 'La situation impose l\'une ou l\'autre — pas les deux au hasard.' },
      ],
      retenir: 'L\'ÉVITEMENT EST UNE ACTION RAPIDE SUR LA TRAJECTOIRE, MAIS ELLE DOIT RESTER MAÎTRISÉE.',
    },
    {
      num: '10', kicker: 'Regard qui sauve', title: 'Ton regard déclenche ton évitement',
      lead: "Ne fixe pas l'obstacle : si toute ton attention reste dessus, tu risques de continuer à construire ta trajectoire vers lui. Tu identifies l'obstacle, puis ton regard cherche immédiatement la zone libre.",
      type: 'timeline',
      steps: [
        { time: '1', label: 'OBSTACLE', desc: 'Je le détecte, sans m\'y accrocher visuellement.' },
        { time: '2', label: 'ZONE LIBRE', desc: 'Mon regard cherche immédiatement où passer.' },
        { time: '3', label: 'RETOUR EN TRAJECTOIRE', desc: 'Mon regard prépare déjà la sortie du contournement.' },
      ],
      retenir: 'TU IDENTIFIES LE DANGER, MAIS TU CONSTRUIS TA TRAJECTOIRE VERS LA SOLUTION.',
    },
    {
      num: '11', kicker: 'Direction à allure élevée', title: 'Tu découvres l\'action de direction à allure plus élevée',
      lead: "À une allure suffisante, le changement rapide de trajectoire demande une action précise et brève sur le guidon : c'est le contre-braquage. Pour initier l'inclinaison, tu exerces une action momentanée sur le guidon du côté vers lequel tu souhaites incliner la moto. Ce n'est pas une lutte contre l'effet gyroscopique : c'est l'action de direction qui crée le roulis nécessaire.",
      type: 'cards3',
      cards: [
        { title: 'ACTION BRÈVE', desc: 'Sur le guidon du côté où l\'on veut incliner.' },
        { title: 'ROULIS', desc: 'La moto s\'incline et change de trajectoire.' },
        { title: 'ADAPTATION', desc: 'Rien à voir avec le braquage d\'un demi-tour lent.' },
      ],
      retenir: 'BASSE ALLURE ≠ ALLURE PLUS ÉLEVÉE. LA DIRECTION SE CONSTRUIT DIFFÉREMMENT.',
    },
    {
      num: '12', kicker: 'Évitement complet', title: 'Tu construis ton évitement complet',
      lead: "Une fois la difficulté passée, ton travail n'est pas terminé : tu dois retrouver une trajectoire stable. Le mouvement doit devenir précis, volontaire et fluide — ni panique, ni brutalité inutile.",
      type: 'timeline',
      steps: [
        { time: '1', label: 'REGARDE LA SOLUTION', desc: 'Regard porté sur la zone libre.' },
        { time: '2', label: 'DÉCLENCHE', desc: 'Action de direction précise et brève.' },
        { time: '3', label: 'LAISSE INCLINER', desc: 'La moto s\'oriente sans que tu luttes.' },
        { time: '4', label: 'FRANCHIS', desc: 'Tu passes la zone en conservant le regard loin.' },
        { time: '5', label: 'RETOUR', desc: 'Tu construis ta ligne de sortie.' },
        { time: '6', label: 'STABILISE', desc: 'Trajectoire retrouvée, moto stable.' },
      ],
      retenir: 'UNE FOIS L\'ÉVITEMENT FAIT, TON TRAVAIL N\'EST PAS TERMINÉ. TU DOIS STABILISER.',
    },
    {
      num: '13', kicker: 'Enchaînements', title: 'Tu commences à enchaîner les compétences',
      lead: "Tu vas maintenant utiliser plusieurs compétences successivement : départ → allure lente → demi-tour → accélération → changement de rapport → freinage → évitement → arrêt. Je parle moins. Je t'indique l'exercice, puis tu le construis. Si tu fais une erreur, nous analysons ensemble.",
      type: 'list',
      list: [
        { label: 'OÙ S\'EST DÉGRADÉ ?', desc: 'À quel moment précis l\'enchaînement a basculé ?' },
        { label: 'REGARD EN RETARD ?', desc: 'Ton regard était-il en retard sur ce qui arrivait ?' },
        { label: 'PRÉPARATION AMONT ?', desc: 'As-tu préparé suffisamment tôt ?' },
        { label: 'CHARGE COGNITIVE ?', desc: 'Une action t\'a-t-elle demandé trop d\'attention ?' },
        { label: 'ERREUR EN CHAÎNE ?', desc: 'As-tu voulu rattraper une erreur précédente au lieu de repartir propre ?' },
      ],
      retenir: 'TU DEVRAS UTILISER TES COMPÉTENCES ENSEMBLE ET AU BON MOMENT.',
    },
    {
      num: '14', kicker: 'Bilan Plateau N°3', title: 'Ton bilan plateau n°3',
      lead: "Ton bilan devient plus exigeant. Je veux que tu sois capable de distinguer ce que tu sais faire de ce que tu sais faire régulièrement.",
      type: 'bilan',
      questions: [
        'Retrouver rapidement ma maîtrise à basse allure ?',
        "Passer d'une allure lente à une allure plus importante ?",
        'Accélérer sans dégrader ma trajectoire ?',
        'Changer de rapport sans regarder mes commandes ?',
        'Préparer mon freinage suffisamment tôt ?',
        'Freiner avec progressivité et efficacité ?',
        'Maîtriser ma trajectoire pendant mon freinage ?',
        'Arrêter ma moto dans une zone déterminée ?',
        "Identifier rapidement une zone d'évitement ?",
        "Détourner mon regard de l'obstacle pour chercher la solution ?",
        'Provoquer un changement de trajectoire précis à allure adaptée ?',
        'Effectuer un évitement puis stabiliser ma moto ?',
        'Enchaîner basse allure, accélération, freinage et évitement ?',
        "Analyser la cause d'une erreur ?",
        'Recommencer sans reproduire systématiquement la même erreur ?',
      ],
      blocks: [
        { title: '🟢 JE MAÎTRISE ET JE RÉPÈTE', color: 'green' },
        { title: '🟠 JE RÉUSSIS MAIS MANQUE DE RÉGULARITÉ', color: 'amber' },
        { title: '🔴 J\'AI ENCORE BESOIN D\'ACCOMPAGNEMENT', color: 'red' },
      ],
      body: "Puis nous définissons : ce qui est maintenant solide · ce qui manque encore de régularité · l'erreur que je comprends mieux · mon objectif pour la Session N°4. Pendant la session 1 tu as appris à ressentir ta moto. Pendant la session 2 à coordonner tes actions. Aujourd'hui, tu apprends à utiliser cette maîtrise quand l'allure et les exigences augmentent : basse allure → accélération → freinage → évitement → enchaînement.",
      retenir: "LA MAÎTRISE, CE N'EST PAS SEULEMENT SAVOIR FAIRE UN EXERCICE. C'EST SAVOIR CHOISIR ET COORDONNER LA BONNE ACTION AU BON MOMENT.",
    },
  ],
};
